import { buildCoverageIdToBlockName } from "./buildContextMaps";
import { financialsRowKinds } from "./types";

import type {
  CoverageRowKind,
  FinancialsRow,
  FinancialsRowKind,
  TotalsRowKind,
} from "./types";
import type {
  FinancialsTableResponse,
  FundingType,
  CustomTotalsBlock,
  TotalsBlock,
} from "@platform/api";

/**
 * Constructs a stable, inspectable row id. Coverage rows encode the
 * coverage id into the string so the sidecar map can resolve which
 * coverage a value belongs to without putting coverage identity onto
 * the row data itself.
 */
export const makeRowId = (
  kind: FinancialsRowKind,
  coverageId?: string,
  blockId?: string,
): string => {
  if (coverageId !== undefined) return `coverage:${coverageId}:${kind}`;
  const block = blockId ?? "default";

  return `totals:${block}:${kind}`;
};

export type BuiltRows = {
  rows: FinancialsRow[];
  bottomRows: FinancialsRow[];
  coverageByRowId: ReadonlyMap<string, string>;
};

const buildCoverageRows = (
  coverageId: string,
  rowKinds: CoverageRowKind[],
  coverageByRowId: Map<string, string>,
  blockNames: readonly string[] | undefined,
): FinancialsRow[] => {
  const headerRow: FinancialsRow = {
    rowId: makeRowId(financialsRowKinds.COVERAGE_HEADER, coverageId),
    kind: financialsRowKinds.COVERAGE_HEADER,
    coverageId,
    blockNames,
  };

  const dataRows = rowKinds.map((kind) => {
    const rowId = makeRowId(kind, coverageId);
    coverageByRowId.set(rowId, coverageId);

    return { rowId, kind, coverageId, blockNames };
  });

  return [headerRow, ...dataRows];
};

const buildTotalsRows = (
  rowKinds: TotalsRowKind[],
  blockId: string,
): FinancialsRow[] =>
  rowKinds.map((kind) => ({
    rowId: makeRowId(kind, undefined, blockId),
    kind,
    blockId,
  }));

const SELF_FUNDED_ROW_KINDS: CoverageRowKind[] = [
  financialsRowKinds.ANNUAL_PREMIUM,
  financialsRowKinds.NOTES,
];

const coverageRowKindOrder = (
  showSavings: boolean,
  fundingType: FundingType,
): CoverageRowKind[] => {
  if (fundingType === "selfFunded") return SELF_FUNDED_ROW_KINDS;

  const kinds: CoverageRowKind[] = [
    financialsRowKinds.ANNUAL_PREMIUM,
    financialsRowKinds.VARIANCE,
  ];
  if (showSavings) kinds.push(financialsRowKinds.DOLLAR_SAVINGS);
  kinds.push(financialsRowKinds.NOTES);

  return kinds;
};

const totalsRowKindOrder = (
  showSavings: boolean,
  block: TotalsBlock,
): TotalsRowKind[] => {
  const kinds: TotalsRowKind[] = [
    financialsRowKinds.TOTALS_HEADER,
    financialsRowKinds.TOTAL_ANNUAL_PREMIUM,
    financialsRowKinds.TOTAL_VARIANCE,
  ];
  if (showSavings) kinds.push(financialsRowKinds.TOTAL_DOLLAR_SAVINGS);
  if (!block.creditsRow.hidden) {
    kinds.push(financialsRowKinds.TOTAL_CREDITS);
    kinds.push(financialsRowKinds.TOTAL_PREMIUM_WITH_CREDITS);
  }
  kinds.push(financialsRowKinds.TOTAL_NOTES);

  return kinds;
};

/**
 * Produces AG Grid row data plus the sidecar `rowId → coverageId` map
 * that valueGetters and cell renderers consume to resolve a data
 * row's coverage.
 */
export const buildFinancialsRowData = (
  payload: FinancialsTableResponse,
): BuiltRows => {
  const coverageByRowId = new Map<string, string>();
  const showSavings = payload.brokerConfig.showDollarSavings;
  const coverageIdToBlockName = buildCoverageIdToBlockName(
    payload.totalsBlocks,
  );

  const rows = payload.coverages
    .filter((c) => !c.hidden)
    .flatMap((coverage) => {
      const kinds = coverageRowKindOrder(showSavings, coverage.fundingType);
      const blockNames = coverageIdToBlockName.get(coverage.id);

      return buildCoverageRows(coverage.id, kinds, coverageByRowId, blockNames);
    });

  const customBlocks = payload.totalsBlocks
    .filter((b): b is CustomTotalsBlock => b.kind === "custom")
    .sort((a, b) => a.order - b.order);

  const defaultBlock = payload.totalsBlocks.find((b) => b.kind === "default");

  const bottomRows = [
    ...customBlocks.flatMap((block) =>
      buildTotalsRows(totalsRowKindOrder(showSavings, block), block.id),
    ),
    ...(defaultBlock
      ? buildTotalsRows(
          totalsRowKindOrder(showSavings, defaultBlock),
          "default",
        )
      : []),
  ];

  return { rows, bottomRows, coverageByRowId };
};
